import React, { useState, useEffect } from 'react';
import { 
  FiCalendar, 
  FiPlus, 
  FiSearch, 
  FiFilter, 
  FiEdit2, 
  FiTrash2, 
  FiClock,
  FiUser,
  FiUserCheck,
  FiX,
  FiSave,
  FiActivity
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from '../utils/axiosConfig';
import LoadingSpinner from '../components/LoadingSpinner';
import styles from './PacientesNew.module.css';

const Consultas = () => {
  const [consultas, setConsultas] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todas');
  const [showModal, setShowModal] = useState(false);
  const [editingConsulta, setEditingConsulta] = useState(null);
  const [formData, setFormData] = useState({
    paciente_id: '',
    medico_id: '',
    data_consulta: '',
    horario: '',
    tipo_consulta: 'consulta',
    status: 'agendada',
    observacoes: ''
  });

  useEffect(() => {
    fetchConsultas();
    fetchPacientes();
    fetchMedicos();
  }, []);

  const fetchConsultas = async () => {
    try {
      const response = await axios.get('/medical/consultas');
      if (response.data.success) {
        setConsultas(response.data.data.consultas || []);
      }
    } catch (error) {
      console.error('Erro ao carregar consultas:', error);
      toast.error('Erro ao carregar consultas');
    } finally {
      setLoading(false);
    }
  };

  const fetchPacientes = async () => {
    try {
      const response = await axios.get('/medical/pacientes');
      if (response.data.success) {
        setPacientes(response.data.data.pacientes || []);
      }
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
    }
  };

  const fetchMedicos = async () => {
    try {
      const response = await axios.get('/medical/medicos');
      if (response.data.success) {
        setMedicos(response.data.data.medicos || []);
      }
    } catch (error) {
      console.error('Erro ao carregar médicos:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      paciente_id: '',
      medico_id: '',
      data_consulta: '',
      horario: '',
      tipo_consulta: 'consulta',
      status: 'agendada',
      observacoes: ''
    });
    setEditingConsulta(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingConsulta) {
        const response = await axios.put(`/medical/consultas/${editingConsulta.id}`, formData);
        if (response.data.success) {
          toast.success('Consulta atualizada com sucesso!');
        }
      } else {
        const response = await axios.post('/medical/consultas', formData);
        if (response.data.success) {
          toast.success('Consulta agendada com sucesso!');
        }
      }
      
      setShowModal(false);
      resetForm();
      fetchConsultas();
    } catch (error) {
      console.error('Erro ao salvar consulta:', error);
      toast.error(editingConsulta ? 'Erro ao atualizar consulta' : 'Erro ao agendar consulta');
    }
  };

  const handleEdit = (consulta) => {
    setEditingConsulta(consulta);
    setFormData({
      paciente_id: consulta.paciente_id || '',
      medico_id: consulta.medico_id || '',
      data_consulta: consulta.data_consulta ? consulta.data_consulta.split('T')[0] : '',
      horario: consulta.horario || '',
      tipo_consulta: consulta.tipo_consulta || 'consulta',
      status: consulta.status || 'agendada',
      observacoes: consulta.observacoes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja cancelar esta consulta?')) {
      try {
        const response = await axios.delete(`/medical/consultas/${id}`);
        if (response.data.success) {
          toast.success('Consulta cancelada com sucesso!');
          fetchConsultas();
        }
      } catch (error) {
        console.error('Erro ao cancelar consulta:', error);
        toast.error('Erro ao cancelar consulta');
      }
    }
  };

  const filteredConsultas = consultas.filter(consulta => {
    const matchSearch = 
      (consulta.paciente_nome && consulta.paciente_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (consulta.medico_nome && consulta.medico_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (consulta.tipo_consulta && consulta.tipo_consulta.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchStatus = statusFilter === 'todas' || consulta.status === statusFilter;
    
    return matchSearch && matchStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'agendada': return 'info';
      case 'confirmada': return 'success';
      case 'realizada': return 'primary';
      case 'cancelada': return 'error';
      default: return 'neutral';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'agendada': return 'Agendada';
      case 'confirmada': return 'Confirmada';
      case 'realizada': return 'Realizada';
      case 'cancelada': return 'Cancelada';
      default: return status;
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <FiCalendar />
            Consultas
          </h1>
          <p className={styles.subtitle}>
            Gerencie os agendamentos e consultas da clínica
          </p>
        </div>
        <button 
          className="formButton primary"
          onClick={() => setShowModal(true)}
        >
          <FiPlus />
          Nova Consulta
        </button>
      </div>

      {/* Filtros */}
      <div className={styles.filters}>
        <div className="formInputWrapper" style={{ flex: 1, maxWidth: '400px' }}>
          <FiSearch className="formInputIcon" />
          <input
            type="text"
            placeholder="Buscar por paciente, médico ou tipo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="formInput"
          />
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FiFilter />
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="formSelect"
            style={{ minWidth: '150px' }}
          >
            <option value="todas">Todas</option>
            <option value="agendada">Agendadas</option>
            <option value="confirmada">Confirmadas</option>
            <option value="realizada">Realizadas</option>
            <option value="cancelada">Canceladas</option>
          </select>
        </div>
      </div>

      {/* Lista de consultas */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Data/Hora</th>
              <th>Paciente</th>
              <th>Médico</th>
              <th>Tipo</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredConsultas.length === 0 ? (
              <tr>
                <td colSpan="6" className={styles.emptyState}>
                  <FiCalendar />
                  <p>Nenhuma consulta encontrada</p>
                  <small>Clique em "Nova Consulta" para agendar</small>
                </td>
              </tr>
            ) : (
              filteredConsultas.map((consulta) => (
                <tr key={consulta.id}>
                  <td>
                    <div className={styles.dateTime}>
                      <div className={styles.date}>
                        {consulta.data_consulta ? 
                          new Date(consulta.data_consulta).toLocaleDateString('pt-BR') : 
                          'Data não informada'
                        }
                      </div>
                      <div className={styles.time}>
                        <FiClock />
                        {consulta.horario || '--:--'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.patientInfo}>
                      <FiUser />
                      {consulta.paciente_nome || 'Paciente não identificado'}
                    </div>
                  </td>
                  <td>
                    <div className={styles.doctorInfo}>
                      <FiUserCheck />
                      {consulta.medico_nome || 'Médico não identificado'}
                    </div>
                  </td>
                  <td>
                    <span className={styles.consultationType}>
                      {consulta.tipo_consulta || 'Consulta'}
                    </span>
                  </td>
                  <td>
                    <span className={`${styles.badge} ${styles[getStatusColor(consulta.status)]}`}>
                      {getStatusText(consulta.status)}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={`${styles.button} ${styles.small} ${styles.outline}`}
                        onClick={() => handleEdit(consulta)}
                        title="Editar consulta"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className={`${styles.button} ${styles.small} ${styles.danger}`}
                        onClick={() => handleDelete(consulta.id)}
                        title="Cancelar consulta"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>
                {editingConsulta ? 'Editar Consulta' : 'Nova Consulta'}
              </h2>
              <button
                className={styles.closeButton}
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                <FiX />
              </button>
            </div>
            <div className={styles.modalBody}>
            <form onSubmit={handleSubmit} className="form">
              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel" htmlFor="paciente_id">Paciente</label>
                    <div className="formInputWrapper">
                      <FiUser className="formInputIcon" />
                      <select
                        id="paciente_id"
                        name="paciente_id"
                        value={formData.paciente_id}
                        onChange={handleInputChange}
                        className="formSelect"
                        required
                      >
                        <option value="">Selecionar paciente</option>
                        {pacientes.map(paciente => (
                          <option key={paciente.id} value={paciente.id}>
                            {paciente.nome_completo}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel" htmlFor="medico_id">Médico</label>
                    <div className="formInputWrapper">
                      <FiUserCheck className="formInputIcon" />
                      <select
                        id="medico_id"
                        name="medico_id"
                        value={formData.medico_id}
                        onChange={handleInputChange}
                        className="formSelect"
                        required
                      >
                        <option value="">Selecionar médico</option>
                        {medicos.map(medico => (
                          <option key={medico.id} value={medico.id}>
                            {medico.nome_completo} - {medico.especialidade}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel" htmlFor="data_consulta">Data da Consulta</label>
                    <div className="formInputWrapper">
                      <FiCalendar className="formInputIcon" />
                      <input
                        type="date"
                        id="data_consulta"
                        name="data_consulta"
                        value={formData.data_consulta}
                        onChange={handleInputChange}
                        className="formInput"
                        required
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel" htmlFor="horario">Horário</label>
                    <div className="formInputWrapper">
                      <FiClock className="formInputIcon" />
                      <input
                        type="time"
                        id="horario"
                        name="horario"
                        value={formData.horario}
                        onChange={handleInputChange}
                        className="formInput"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel" htmlFor="tipo_consulta">Tipo de Consulta</label>
                    <div className="formInputWrapper">
                      <FiClock className="formInputIcon" />
                      <select
                        id="tipo_consulta"
                        name="tipo_consulta"
                        value={formData.tipo_consulta}
                        onChange={handleInputChange}
                        className="formSelect"
                      >
                        <option value="consulta">Consulta</option>
                        <option value="retorno">Retorno</option>
                        <option value="urgencia">Urgência</option>
                        <option value="exame">Exame</option>
                        <option value="procedimento">Procedimento</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel" htmlFor="status">Status</label>
                    <div className="formInputWrapper">
                      <FiActivity className="formInputIcon" />
                      <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="formSelect"
                      >
                        <option value="agendada">Agendada</option>
                        <option value="confirmada">Confirmada</option>
                        <option value="realizada">Realizada</option>
                        <option value="cancelada">Cancelada</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="formGroup">
                <label className="formLabel" htmlFor="observacoes">Observações</label>
                <textarea
                  id="observacoes"
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleInputChange}
                  className="formTextarea"
                  rows="3"
                  placeholder="Observações sobre a consulta..."
                />
              </div>

              <div className="formButtonGroup right">
                <button
                  type="button"
                  className="formButton secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="formButton primary"
                >
                  <FiSave />
                  {editingConsulta ? 'Atualizar' : 'Agendar'}
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default Consultas;